/**
 * The calculator's wire contract — backend capability C3.
 *
 * The client computes NOTHING it was not handed. The backend's intelligence
 * layer decides that a turn warrants a calculation, picks the formula, and
 * supplies every constant, unit, bound and label. This file is the shape of
 * that handover; `QuantityCalculatorCard` is a pure renderer over it.
 *
 * Why the formula set is CLOSED: a formula is arithmetic, and arithmetic that
 * arrives as data is arithmetic nobody reviewed. The backend chooses one of the
 * named formulas below and sends its inputs; a name this client does not know
 * renders nothing rather than guessing. Adding a formula is a deliberate
 * frontend change with a test, in step with the backend.
 */
import type { LocalizedText } from "@/lib/garden-api";

export type CalculatorFormula = "coverage_mass" | "area_units" | "perimeter_units";

export interface CalculatorOption {
  value: string;
  label: LocalizedText;
}

export interface CalculatorInput {
  key: string;
  label: LocalizedText;
  kind: "number" | "select";
  unit: LocalizedText | null;
  default: number | string;
  min: number | null;
  max: number | null;
  options: CalculatorOption[] | null;
}

export interface CalculatorSpec {
  title: LocalizedText;
  /** The constants, stated in words, so the user can check the number. */
  caption: LocalizedText;
  inputs: CalculatorInput[];
  formula: CalculatorFormula;
  constants: Record<string, number>;
  /** Interpolates `{{n}}`. */
  result_label: LocalizedText;
  result_unit: LocalizedText;
  /** Decimal-as-string. Null when the line has no priced match. */
  price_per_unit_kgs: string | null;
}

export const FORMULAS: Record<
  CalculatorFormula,
  (values: Record<string, number>, constants: Record<string, number>) => number
> = {
  // Repo tsconfig sets `noUncheckedIndexedAccess`, so every `Record<string,
  // number>` lookup below types as `number | undefined` even though the
  // caller always supplies a full set. `?? NaN` keeps the intended
  // semantics for a genuinely absent key: NaN propagates through the
  // arithmetic and the card's `Number.isFinite` guard turns it into "render
  // nothing" (see the "missing constant" decision in QuantityCalculatorCard)
  // rather than the compiler silently trusting an unchecked lookup.
  /** Dry mixes: area × thickness × coverage rate ÷ package mass. */
  coverage_mass: (v, c) =>
    Math.ceil(
      ((v.area ?? NaN) * (v.thickness ?? NaN) * (c.coverage_rate ?? NaN)) /
        (c.package_kg ?? NaN),
    ),
  /**
   * Sheet goods: area × waste allowance ÷ area per unit.
   *
   * `waste_pct ?? 0` is a DELIBERATE, DOCUMENTED exception to "every absent
   * constant renders nothing" (backend spec C3 names `waste_pct` as the one
   * contractually optional constant in the whole formula set — every other
   * missing constant, here and in the other two formulas, still propagates
   * NaN and renders nothing). An absent `waste_pct` computes with zero
   * cutting allowance, which UNDER-buys sheet goods — the worst-consequence
   * failure mode for this formula, so the backend is expected to send it
   * explicitly (even as `0`) rather than omit it; this fallback is a last
   * resort, not licence to omit it upstream.
   */
  area_units: (v, c) =>
    Math.ceil(
      ((v.area ?? NaN) * (1 + (c.waste_pct ?? 0) / 100)) / (c.unit_area ?? NaN),
    ),
  /** Skirting, cornice: perimeter ÷ length per unit. */
  perimeter_units: (v, c) => Math.ceil((v.perimeter ?? NaN) / (c.unit_length ?? NaN)),
};

export function isKnownFormula(name: string): name is CalculatorFormula {
  return Object.prototype.hasOwnProperty.call(FORMULAS, name);
}
